import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

const TOUR_KEY = 'mentoria_tour_v2';
const TOOLTIP_W = 320;
const SPOT_PAD = 12;

interface TourStep {
  path: string | null;
  target: string | null;
  icon: string;
  gradient: string;
  title: string;
  subtitle: string;
  description: string;
}

interface Rect { x: number; y: number; w: number; h: number }

const STEPS: TourStep[] = [
  {
    path: null,
    target: null,
    icon: 'waving_hand',
    gradient: 'linear-gradient(135deg,#6366f1,#9333ea)',
    title: 'Welcome to Mentoria!',
    subtitle: 'Your tech interview prep platform',
    description:
      "We'll walk you through the main sections in just a few steps. You can relaunch this tour anytime from the sidebar.",
  },
  {
    path: '/dashboard',
    target: 'tour-quick-actions',
    icon: 'dashboard',
    gradient: 'linear-gradient(135deg,#6366f1,#4f46e5)',
    title: 'Quick Actions',
    subtitle: 'Dashboard',
    description:
      'Jump to any section in one click. The dashboard also shows your overall progress and your next recommended step.',
  },
  {
    path: '/training',
    target: 'tour-training-tabs',
    icon: 'school',
    gradient: 'linear-gradient(135deg,#6366f1,#4f46e5)',
    title: 'Training',
    subtitle: 'Modules · Algorithms · Interview Prep',
    description:
      'Follow video modules, practice coding, and run AI-powered system design interviews. Reach 85% completion to unlock expert verification.',
  },
  {
    path: '/assistant',
    target: 'tour-chat-input',
    icon: 'auto_awesome',
    gradient: 'linear-gradient(135deg,#ec4899,#db2777)',
    title: 'AI Assistant',
    subtitle: 'Your mentor, available 24/7',
    description:
      'Ask any technical question, paste a job description to create an application automatically, or request feedback on your code.',
  },
  {
    path: '/jobs',
    target: 'tour-add-job',
    icon: 'ads_click',
    gradient: 'linear-gradient(135deg,#10b981,#059669)',
    title: 'Job Tracker',
    subtitle: 'Manage your job search pipeline',
    description:
      'Log each application by pasting the full JD. The AI extracts the company and role, and you can track progress from Applied → HR → Tech → Offer.',
  },
  {
    path: '/cv',
    target: 'tour-cv-upload',
    icon: 'description',
    gradient: 'linear-gradient(135deg,#f59e0b,#d97706)',
    title: 'CV Optimizer',
    subtitle: 'AI-powered resume analysis',
    description:
      'Upload your CV and get an ATS score, critical issues, and detailed improvement suggestions. LinkedIn profile analysis is also available.',
  },
  {
    path: null,
    target: null,
    icon: 'rocket_launch',
    gradient: 'linear-gradient(135deg,#8b5cf6,#6d28d9)',
    title: "You're all set!",
    subtitle: 'Your journey starts here',
    description:
      'Recommended order: Training → Algorithms → CV → Jobs. The dashboard will always point you to your next step. Good luck!',
  },
];

// ─── Spotlight SVG overlay ─────────────────────────────────────────────────────

function SpotlightOverlay({ rect }: { rect: Rect }) {
  const x = rect.x - SPOT_PAD;
  const y = rect.y - SPOT_PAD;
  const w = rect.w + SPOT_PAD * 2;
  const h = rect.h + SPOT_PAD * 2;

  return (
    <svg
      className="pointer-events-none fixed inset-0 z-[185]"
      style={{ width: '100vw', height: '100vh', overflow: 'visible' }}
    >
      <defs>
        <mask id="tour-mask">
          <rect width="100vw" height="100vh" fill="white" />
          <rect x={x} y={y} width={w} height={h} rx={14} fill="black" />
        </mask>
      </defs>
      <rect width="100vw" height="100vh" fill="rgba(0,0,0,0.72)" mask="url(#tour-mask)" />
      <rect
        x={x} y={y} width={w} height={h} rx={14}
        fill="none"
        stroke="rgba(99,102,241,0.9)"
        strokeWidth={2.5}
        style={{ filter: 'drop-shadow(0 0 8px rgba(99,102,241,0.55))' }}
      />
    </svg>
  );
}

// ─── Tooltip for steps with a target element ───────────────────────────────────

function TourTooltip({
  step, rect, stepIndex, total, onNext, onPrev, onSkip, isFirst, isLast,
}: {
  step: TourStep; rect: Rect; stepIndex: number; total: number;
  onNext: () => void; onPrev: () => void; onSkip: () => void;
  isFirst: boolean; isLast: boolean;
}) {
  const spotTop = rect.y - SPOT_PAD;
  const spotBottom = rect.y + rect.h + SPOT_PAD;
  const elCenterX = rect.x + rect.w / 2;

  const estimatedH = 230;
  const gap = 14;
  const spaceBelow = window.innerHeight - spotBottom;
  const spaceAbove = spotTop;
  // Prefer below; fall back to below even when tight if placing above would go off-screen
  const placeBelow = spaceBelow >= estimatedH + gap || spaceAbove < estimatedH + gap;

  const rawTop = placeBelow ? spotBottom + gap : spotTop - estimatedH - gap;
  const tooltipTop = Math.max(16, Math.min(rawTop, window.innerHeight - estimatedH - 16));
  const tooltipLeft = Math.max(16, Math.min(elCenterX - TOOLTIP_W / 2, window.innerWidth - TOOLTIP_W - 16));
  const arrowX = Math.max(16, Math.min(elCenterX - tooltipLeft, TOOLTIP_W - 24));

  const bgColor = 'rgb(var(--color-surface-container-low))';

  return (
    <div
      className="fixed z-[195] rounded-2xl bg-surface-container-low shadow-2xl border border-outline-variant/20 p-5 space-y-4"
      style={{ top: tooltipTop, left: tooltipLeft, width: TOOLTIP_W }}
    >
      {/* Arrow */}
      {placeBelow ? (
        <span
          className="absolute"
          style={{ top: -8, left: arrowX - 8, width: 0, height: 0,
            borderLeft: '8px solid transparent', borderRight: '8px solid transparent',
            borderBottom: `8px solid ${bgColor}` }}
        />
      ) : (
        <span
          className="absolute"
          style={{ bottom: -8, left: arrowX - 8, width: 0, height: 0,
            borderLeft: '8px solid transparent', borderRight: '8px solid transparent',
            borderTop: `8px solid ${bgColor}` }}
        />
      )}

      {/* Progress dots + skip */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          {Array.from({ length: total }).map((_, i) => (
            <div
              key={i}
              className={`h-1.5 rounded-full transition-all duration-300 ${
                i === stepIndex ? 'w-5 bg-primary' : 'w-1.5 bg-surface-container-highest'
              }`}
            />
          ))}
        </div>
        <button onClick={onSkip} className="text-[11px] text-on-surface-variant hover:text-on-surface transition-colors">
          Skip
        </button>
      </div>

      {/* Icon + title */}
      <div className="flex items-start gap-3">
        <div
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-white shadow-md"
          style={{ background: step.gradient }}
        >
          <span className="material-symbols-outlined text-xl" style={{ fontVariationSettings: "'FILL' 1" }}>
            {step.icon}
          </span>
        </div>
        <div className="min-w-0">
          <p className="text-[10px] font-bold uppercase tracking-widest text-primary">{step.subtitle}</p>
          <p className="text-base font-extrabold text-on-surface leading-tight">{step.title}</p>
        </div>
      </div>

      {/* Description */}
      <p className="text-xs leading-relaxed text-on-surface-variant">{step.description}</p>

      {/* Buttons */}
      <div className="flex gap-2">
        {!isFirst && (
          <button
            onClick={onPrev}
            className="flex items-center gap-1 rounded-xl bg-surface-container-high px-3 py-2 text-xs font-bold text-on-surface transition-colors hover:bg-surface-container-highest"
          >
            <span className="material-symbols-outlined text-sm">navigate_before</span>
            Back
          </button>
        )}
        <button
          onClick={onNext}
          className="flex flex-1 items-center justify-center gap-1.5 rounded-xl py-2 text-xs font-bold text-white shadow-md transition-all hover:opacity-90"
          style={{ background: step.gradient }}
        >
          {isLast ? 'Get started' : 'Next'}
          {!isLast && <span className="material-symbols-outlined text-sm">navigate_next</span>}
        </button>
      </div>
    </div>
  );
}

// ─── Centered modal for steps without a target ─────────────────────────────────

function CenterModal({
  step, stepIndex, total, onNext, onPrev, onSkip, isFirst, isLast,
}: {
  step: TourStep; stepIndex: number; total: number;
  onNext: () => void; onPrev: () => void; onSkip: () => void;
  isFirst: boolean; isLast: boolean;
}) {
  return (
    <div className="fixed inset-0 z-[195] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/75 backdrop-blur-sm" />
      <div className="relative w-full max-w-sm rounded-3xl bg-surface-container-low shadow-2xl overflow-hidden">
        {/* Progress bar */}
        <div className="h-1 bg-surface-container-highest">
          <div
            className="h-full bg-primary transition-all duration-500"
            style={{ width: `${((stepIndex + 1) / total) * 100}%` }}
          />
        </div>

        <div className="p-8 space-y-5">
          <div className="flex justify-center">
            <div
              className="flex h-20 w-20 items-center justify-center rounded-2xl text-white shadow-lg"
              style={{ background: step.gradient }}
            >
              <span className="material-symbols-outlined text-4xl" style={{ fontVariationSettings: "'FILL' 1" }}>
                {step.icon}
              </span>
            </div>
          </div>

          <div className="text-center space-y-1.5">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary">{step.subtitle}</p>
            <h2 className="text-2xl font-extrabold text-on-surface">{step.title}</h2>
            <p className="text-sm text-on-surface-variant leading-relaxed">{step.description}</p>
          </div>

          {/* Dots */}
          <div className="flex justify-center gap-1.5">
            {Array.from({ length: total }).map((_, i) => (
              <div
                key={i}
                className={`h-1.5 rounded-full transition-all duration-300 ${
                  i === stepIndex ? 'w-6 bg-primary' : 'w-1.5 bg-surface-container-highest'
                }`}
              />
            ))}
          </div>

          <div className="flex flex-col gap-2">
            <button
              onClick={onNext}
              className="flex w-full items-center justify-center gap-2 rounded-xl py-3 text-sm font-bold text-white shadow-md hover:opacity-90 transition-opacity"
              style={{ background: step.gradient }}
            >
              {isLast ? 'Get started' : 'Next'}
              {!isLast && <span className="material-symbols-outlined text-sm">navigate_next</span>}
            </button>
            {!isFirst && (
              <button
                onClick={onPrev}
                className="flex w-full items-center justify-center gap-2 rounded-xl bg-surface-container-high py-3 text-sm font-bold text-on-surface hover:bg-surface-container-highest transition-colors"
              >
                <span className="material-symbols-outlined text-sm">navigate_before</span>
                Back
              </button>
            )}
            {!isLast && (
              <button
                onClick={onSkip}
                className="text-xs text-on-surface-variant hover:text-on-surface transition-colors py-1"
              >
                Skip tour
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Main tour component ───────────────────────────────────────────────────────

export function AppTour({ onClose }: { onClose: () => void }) {
  const [stepIndex, setStepIndex] = useState(0);
  const [targetRect, setTargetRect] = useState<Rect | null>(null);
  const [ready, setReady] = useState(false);
  const navigate = useNavigate();

  const step = STEPS[stepIndex];
  const isFirst = stepIndex === 0;
  const isLast = stepIndex === STEPS.length - 1;

  // Navigate to the right page and wait for the target element
  useEffect(() => {
    setReady(false);
    setTargetRect(null);

    if (step.path) {
      navigate(step.path);
    }

    if (!step.target) {
      setReady(true);
      return;
    }

    let count = 0;
    const interval = setInterval(() => {
      const el = document.querySelector(`[data-tour="${step.target}"]`) as HTMLElement | null;
      if (el) {
        clearInterval(interval);
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        setTimeout(() => {
          const r = el.getBoundingClientRect();
          setTargetRect({ x: r.left, y: r.top, w: r.width, h: r.height });
          setReady(true);
        }, 350);
        return;
      }
      if (++count >= 30) {
        clearInterval(interval);
        setReady(true);
      }
    }, 100);

    return () => clearInterval(interval);
  }, [stepIndex]);

  // Recompute rect on resize and scroll
  useEffect(() => {
    if (!step.target || !ready) return;
    const update = () => {
      const el = document.querySelector(`[data-tour="${step.target}"]`) as HTMLElement | null;
      if (el) {
        const r = el.getBoundingClientRect();
        setTargetRect({ x: r.left, y: r.top, w: r.width, h: r.height });
      }
    };
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [step.target, ready]);

  const handleNext = useCallback(() => {
    if (isLast) {
      navigate('/dashboard');
      onClose();
    } else {
      setStepIndex((i) => i + 1);
    }
  }, [isLast, onClose, navigate]);

  const handlePrev = useCallback(() => {
    setStepIndex((i) => Math.max(0, i - 1));
  }, []);

  // Loading overlay while navigating / polling for element
  if (!ready) {
    return (
      <div className="fixed inset-0 z-[185] flex items-center justify-center bg-black/70">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  // Steps without a target element → centered modal
  if (!step.target || !targetRect) {
    return (
      <CenterModal
        step={step}
        stepIndex={stepIndex}
        total={STEPS.length}
        onNext={handleNext}
        onPrev={handlePrev}
        onSkip={onClose}
        isFirst={isFirst}
        isLast={isLast}
      />
    );
  }

  // Steps with a target element → spotlight + tooltip
  return (
    <>
      <SpotlightOverlay rect={targetRect} />
      <TourTooltip
        step={step}
        rect={targetRect}
        stepIndex={stepIndex}
        total={STEPS.length}
        onNext={handleNext}
        onPrev={handlePrev}
        onSkip={onClose}
        isFirst={isFirst}
        isLast={isLast}
      />
    </>
  );
}

// ─── Hook ──────────────────────────────────────────────────────────────────────

export function useTour(userId: string | undefined) {
  const [showTour, setShowTour] = useState(false);

  useEffect(() => {
    if (!userId) return;
    const key = `${TOUR_KEY}_${userId}`;
    if (!localStorage.getItem(key)) {
      const t = setTimeout(() => setShowTour(true), 800);
      return () => clearTimeout(t);
    }
  }, [userId]);

  const closeTour = useCallback(() => {
    if (userId) localStorage.setItem(`${TOUR_KEY}_${userId}`, '1');
    setShowTour(false);
  }, [userId]);

  const openTour = useCallback(() => setShowTour(true), []);

  return { showTour, closeTour, openTour };
}
